import * as React from 'react';

import { IRequirementGroup, IRequirementType, RequirementActionType } from '@models/requirements';
import * as Expressions from '@utils/expressions';
import {
    Button,
    Dialog,
    InputTextarea,
    Message,
    Select,
    TabPanel,
    TabView,
    useLoading,
    useTranslation,
} from '@components';

import './ActionExpressionsDialog.scss';

export interface IActionExpressionsDialogProps {
    visible: boolean;
    onHide: () => void;
    onSave: (data: IRequirementGroup | IRequirementType) => void;
    data: IRequirementGroup | IRequirementType;
    isGroup: boolean;
    users?: any[];
    works?: any[];
    getJobs?: Function;
}

export const ACTION_TYPES: { id: RequirementActionType; name: string; description: string; icon: string }[] = [
    {
        id: RequirementActionType.Validate,
        name: 'requirements.actions.validate',
        description: 'requirements.actions.validate.desc',
        icon: 'pi pi-check-square',
    },
    {
        id: RequirementActionType.UploadDocument,
        name: 'requirements.actions.upload',
        description: 'requirements.actions.upload.desc',
        icon: 'pi pi-upload',
    },
    {
        id: RequirementActionType.AddIncidence,
        name: 'requirements.actions.addIncidence',
        description: 'requirements.actions.addIncidence.desc',
        icon: 'pi pi-exclamation-triangle',
    },
    {
        id: RequirementActionType.AddComment,
        name: 'requirements.actions.addComment',
        description: 'requirements.actions.addComment.desc',
        icon: 'pi pi-comments',
    },
    {
        id: RequirementActionType.RemoveDocument,
        name: 'requirements.actions.removeDocument',
        description: 'requirements.actions.removeDocument.desc',
        icon: 'pi pi-trash',
    },
    {
        id: RequirementActionType.ViewHistory,
        name: 'requirements.actions.viewHistory',
        description: 'requirements.actions.viewHistory.desc',
        icon: 'pi pi-clock',
    },
    {
        id: RequirementActionType.CanDownload,
        name: 'requirements.actions.canDownload',
        description: 'requirements.actions.canDownload.desc',
        icon: 'pi pi-download',
    },
];

export function ActionExpressionsDialog({
    visible,
    onHide,
    onSave,
    data,
    isGroup,
    users = [],
    works = [],
    getJobs,
}: IActionExpressionsDialogProps) {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = React.useState<number>(0);
    const [expressions, setExpressions] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
        if (data) {
            const expr: Record<string, string> = {};
            ACTION_TYPES.forEach(action => {
                const key = getExpressionKey(action.id);
                expr[key] = (data as any)[key] || '';
            });
            setExpressions(expr);
        }
    }, [data]);

    const getExpressionKey = (action: RequirementActionType): string => {
        switch (action) {
            case RequirementActionType.Validate:
                return 'expressionValidate';
            case RequirementActionType.UploadDocument:
                return 'expressionUploadDocument';
            case RequirementActionType.AddIncidence:
                return 'expressionAddIncidence';
            case RequirementActionType.AddComment:
                return 'expressionAddComment';
            case RequirementActionType.RemoveDocument:
                return 'expressionRemoveDocument';
            case RequirementActionType.ViewHistory:
                return 'expressionViewHistory';
            case RequirementActionType.CanDownload:
                return 'expressionCanDownload';
            default:
                return '';
        }
    };

    const handleExpressionChange = (action: RequirementActionType, value: string) => {
        const key = getExpressionKey(action);
        setExpressions(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        const updatedData = { ...data };
        ACTION_TYPES.forEach(action => {
            const key = getExpressionKey(action.id);
            (updatedData as any)[key] = expressions[key] || null;
        });
        onSave(updatedData);
    };

    const currentAction = ACTION_TYPES[activeIndex];
    const currentExpression = expressions[getExpressionKey(currentAction.id)] || '';

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={t(isGroup ? 'Edit Group Action Expressions' : 'Edit Requirement Type Action Expressions')}
            className="ActionExpressionsDialog"
            style={{ width: '90vw', maxWidth: '1300px' }}
        >
            <div className="c ActionExpressionsDialog-content">
                <p className="dialog-description">
                    {t('Define who can perform each action using expressions. Use Identity, Job, and other context variables.')}
                </p>

                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                    {ACTION_TYPES.map(action => (
                        <TabPanel
                            key={action.id}
                            header={t(action.name)}
                            leftIcon={action.icon}
                        >
                            <div className="tab-content">
                                <div className="action-description">
                                    {t(action.description)}
                                </div>

                                <div className="expression-section">
                                    <label className="expression-label">
                                        {t('Expression')}
                                    </label>
                                    <InputTextarea
                                        value={currentExpression}
                                        onChange={(e) => handleExpressionChange(action.id, e.target.value)}
                                        className="w-100p"
                                        rows={5}
                                    />
                                </div>

                                {users.length > 0 && works.length > 0 && (
                                    <div className="test-section">
                                        <h4>{t('Test Expression')}</h4>
                                        <TestExpression
                                            expression={currentExpression}
                                            users={users}
                                            works={works}
                                            getJobs={getJobs}
                                        />
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                    ))}
                </TabView>
            </div>

            <div className="dialog-footer">
                <Button
                    label={t('Cancel')}
                    onClick={onHide}
                    className="p-button-text"
                />
                <Button
                    label={t('Save')}
                    onClick={handleSave}
                    className="p-button-primary"
                />
            </div>
        </Dialog>
    );
}

function TestExpression({
    expression,
    users,
    works,
    getJobs,
}: {
    expression: string;
    users: any[];
    works: any[];
    getJobs?: Function;
}) {
    const loading = useLoading(false);
    const { t } = useTranslation();

    const [error, setError] = React.useState<string | undefined>();
    const [result, setResult] = React.useState<any>();
    const [teUser, setTeUser] = React.useState<number | undefined>();
    const [teWork, setTeWork] = React.useState<number | undefined>();
    const [teJob, setTeJob] = React.useState<number | undefined>();
    const [containsJob, setContainsJob] = React.useState<boolean>(false);
    const [jobs, setJobs] = React.useState<any[]>([]);

    React.useEffect(() => {
        setContainsJob(/\bJob\b/i.test(expression));
    }, [expression]);

    const initializeJobs = loading.wrap(async () => {
        if (getJobs && teWork != undefined) {
            const jobsResult = await getJobs(teWork);
            setJobs(jobsResult.data?.work?.jobs || []);
        }
    });

    React.useMemo(() => {
        if (getJobs && teWork != undefined && containsJob) {
            initializeJobs();
        }
    }, [teWork, containsJob]);

    const testExpression = loading.wrap((e: any = null) => {
        setError(undefined);
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        try {
            const work = works.find(w => w.id == teWork);
            const expr = Expressions.parse(expression);
            const r = Expressions.evaluate(expr, {
                identity: users.find(u => u.id == teUser),
                work,
                workId: work?.id,
                job: jobs.find(j => j.id == teJob),
                jobId: teJob,
            });
            if (expr) {
                setResult(r);
            } else {
                setError(t('Invalid expression'));
            }
        } catch (e) {
            setError((e as any).message);
        }
    });

    return (
        <div className='c TestExpression'>
            <div className='r'>
                <Select
                    className='flat'
                    value={teUser}
                    onChange={e => setTeUser(e.value)}
                    options={users}
                    optionLabel={'userName'}
                    filter={true}
                    optionValue={'id'}
                    placeholder={t('User')}
                />
                <Select
                    className='flat'
                    value={teWork}
                    onChange={e => setTeWork(e.value)}
                    options={works}
                    filter={true}
                    optionLabel={'name'}
                    optionValue={'id'}
                    placeholder={t('Work')}
                />
                <Select
                    className='flat'
                    value={teJob}
                    onChange={e => setTeJob(e.value)}
                    options={jobs}
                    filter={true}
                    optionLabel={'name'}
                    optionValue={'id'}
                    placeholder={t('Job')}
                    disabled={!containsJob}
                />
                <span className='e'></span>
                <button
                    disabled={teUser == undefined || teWork == undefined || (containsJob && teJob == undefined)}
                    onClick={testExpression}
                >
                    {t('Test')}
                </button>
                {loading.render()}
            </div>
            {error && <Message severity='error' text={error} />}
            {!error && result != undefined && <Message severity='success' text={JSON.stringify(result)} />}
        </div>
    );
}

export default ActionExpressionsDialog;
